'use client';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { flushSync } from 'react-dom';
import { ProcessStepHeading } from '../components/process-step-heading';
import { useFitScale } from '../lib/desktop-scale';
import { inverseScaleStyle } from '../lib/scale-compensate';
import {
  DESKTOP_MIN_WIDTH_PX,
  MOBILE_REF_WIDTH_PX,
  MOBILE_SECTION_PADDING_PX,
  mobileRefScale,
} from '../lib/mobile-layout';
import {
  IN_PLACE_FROM_STAGE,
  flipFaceSources,
  flipForProgress,
  imageColForStage,
  inPlaceCrossfadeOpacities,
  loaderFillPctFromProgress,
  navStageOpacity,
  progressForStage,
  stepColAt,
} from '../lib/process-progress';
import { PROCESS_STEPS } from '../lib/process-steps';
import {
  DesktopFlipImage,
  ImagePlaceholder,
  MobileProcessVisual,
  MobileStepBody,
  NavLoaderTrack,
  StepCard,
} from './process-visuals';

/*
 * Homepage "How it works" scroll sequence.
 *
 * Pinning is pure CSS: each stage is `position: sticky` inside a tall track, so the browser pins and
 * releases it on the compositor with no JavaScript in the loop. (The previous version toggled
 * `position: fixed` from React state, which landed a frame late on a fast scroll — the page jumped.)
 * Scroll progress is simply derived from where the track is, on every scroll event, so nothing can
 * get out of sync: 0 → 4, the integer part is the step, the fraction is progress within it.
 */

const STEPS = PROCESS_STEPS;
const STEP_COUNT = STEPS.length;
const MAX_STAGE = STEP_COUNT - 1;

// ── Desktop design frame (Figma "Desktop - 7", 1440×888) ─────────────────────────────────────────
/** Content width inside the 1440px canvas (100px margins). */
const DESKTOP_FRAME_W = 1240;
/** One full-screen desktop frame. The pinned stage is scaled to fit the window. */
const DESKTOP_FRAME_H = 888;
/** Header block above the stage: 100px top padding + "The process" + title, minus the nav gap. */
const DESKTOP_HEADER_H = 173;
const NAV_TOP = 24;
/**
 * Nav row: titles are text-title/6xl (60px × 144% = 86.4px line) + 20px gap + 4px loader bar. The design
 * lays them out at their natural widths with 60px gaps in one row that slides left as the sequence
 * advances; the fourth title bleeds off the right edge (the sticky stage clips it at the window).
 */
const NAV_H = 110;
const NAV_TITLE_GAP = 60;
/** Natural title widths at 60px in the design ("Browse projects" … "Withdraw returns"); re-measured in the browser. */
const DESIGN_TITLE_WIDTHS = [419, 302, 369, 465];
/**
 * In the Figma stage frames the row offset is 0 for steps 1 and 2, −248 for step 3 and −495 for step 4,
 * i.e. step 4 is right-aligned to the 1240px frame and step 3 is halfway there. Those rules (rather than
 * the raw numbers) are used so the layout stays right when the real font measures slightly different
 * widths from Figma's.
 */
function navRowOffsets(lefts: number[], widths: number[]): number[] {
  const last = lefts.length - 1;
  const rightAligned = Math.min(0, DESKTOP_FRAME_W - (lefts[last] + widths[last]));
  return [0, 0, rightAligned / 2, rightAligned];
}
/** Content row starts 16px under the nav; with the 688px row and 50px below it the frame is exactly 888px. */
const ROW_TOP = NAV_TOP + NAV_H + 16;
const ROW_H = 688;

// ── Mobile ───────────────────────────────────────────────────────────────────────────────────────
/** Headers sit below the top edge when pinned (matches the natural entry layout). */
const MOBILE_PIN_TOP_PX = 48;
const MOBILE_PIN_BOTTOM_PX = 12;
/** Height of everything in the frame except the illustration (header, loader, gaps, copy). */
const MOBILE_NON_IMAGE_CONTENT_PX = 262;
const MOBILE_IMAGE_MIN_HEIGHT_PX = 280;

function subscribeToDesktopQuery(onChange: () => void): () => void {
  const mq = window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH_PX}px)`);
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

function useIsDesktop(): boolean {
  return useSyncExternalStore(
    subscribeToDesktopQuery,
    () => window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH_PX}px)`).matches,
    () => false,
  );
}

export default function ProcessSection() {
  const desktopTrackRef = useRef<HTMLDivElement | null>(null);
  const desktopRunwayRef = useRef<HTMLDivElement | null>(null);
  const mobileTrackRef = useRef<HTMLDivElement | null>(null);
  const mobileRunwayRef = useRef<HTMLDivElement | null>(null);
  const mobileContentRef = useRef<HTMLDivElement | null>(null);

  const navButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const mobileStepTextRefs = useRef<Array<HTMLDivElement | null>>([]);
  const mobileHeaderTextRef = useRef<HTMLDivElement | null>(null);

  const [stageProgress, setStageProgress] = useState<number>(0);
  const [mobileContentHeight, setMobileContentHeight] = useState(0);
  // Natural width of each nav title at 60px. Starts from the Figma widths (so server and first client
  // render agree) and is re-measured once the real font has loaded.
  const [navTitleWidths, setNavTitleWidths] = useState<number[]>(DESIGN_TITLE_WIDTHS);
  // Tallest step heading+description at their true (counter-scaled) size — see MOBILE_STEP_TEXT_MIN_PX.
  const [mobileStepTextHeight, setMobileStepTextHeight] = useState(0);
  // "The process" + "How it works" at their true (counter-scaled) size — see mobileHeaderTextRef below.
  const [mobileHeaderTextHeight, setMobileHeaderTextHeight] = useState(0);

  const isDesktop = useIsDesktop();
  const scale = useFitScale();
  const mobileScale = useSyncExternalStore(
    (onStoreChange) => {
      window.addEventListener('resize', onStoreChange);
      return () => window.removeEventListener('resize', onStoreChange);
    },
    () => mobileRefScale(window.innerWidth, MOBILE_SECTION_PADDING_PX),
    () => 1,
  );

  const mobileFrameWidth = Math.round(MOBILE_REF_WIDTH_PX * mobileScale);
  const mobileFrameHeight = mobileContentHeight
    ? Math.round(mobileContentHeight * mobileScale)
    : undefined;

  // The mobile frame is authored at 430px and scaled, so measure its unscaled height once it changes.
  useLayoutEffect(() => {
    const el = mobileContentRef.current;
    if (!el) return;
    const measure = () => setMobileContentHeight(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Measure the nav titles' natural widths (offsetWidth ignores the stage's scale transform).
  useLayoutEffect(() => {
    let cancelled = false;
    const measure = (): void => {
      if (cancelled) return;
      setNavTitleWidths((prev) => {
        const next = prev.map((w, i) => {
          const el = navButtonRefs.current[i];
          return el && el.offsetWidth > 0 ? el.offsetWidth : w;
        });
        return next.every((w, i) => w === prev[i]) ? prev : next;
      });
    };
    measure();
    void document.fonts?.ready.then(measure);
    return () => {
      cancelled = true;
    };
  }, []);

  // Each step's heading+description is now counter-scaled to its true, declared size (see
  // inverseScaleStyle below), so it no longer shrinks along with the `min-h-[148px]` reference box
  // that reserves its space — that box is itself ambient-scaled and can end up shorter than the true
  // content on any phone narrower than the 430px reference, clipping into the illustration below it.
  // Measuring the tallest step's real height (offsetHeight ignores the counter-scale, same as
  // offsetWidth above) and using that as the reserved height keeps the two in sync. Depends on
  // mobileFrameWidth because that's what the text wraps against (maxWidth on the same element below),
  // so re-measure whenever it changes.
  useLayoutEffect(() => {
    let cancelled = false;
    const measure = (): void => {
      if (cancelled) return;
      const heights = mobileStepTextRefs.current.map((el) => el?.offsetHeight ?? 0);
      setMobileStepTextHeight(Math.max(0, ...heights));
    };
    measure();
    void document.fonts?.ready.then(measure);
    return () => {
      cancelled = true;
    };
  }, [mobileFrameWidth]);

  // Same mismatch as mobileStepTextHeight above, one level up: "The process" + "How it works" is
  // counter-scaled to its true size, but the `mt-4` gap before the loader bar right after it is NOT
  // (that bar's own width still needs to track the ambient frame like the rest of the illustration) —
  // so on any phone narrower than the 430px reference, the text paints taller than the layout space
  // the un-scaled flex flow reserved for it, and visually swallows that gap, touching the bar below.
  // Reserving the text's true measured height (converted back to reference-space) keeps the gap intact
  // without needing to touch the loader bar itself. Doesn't depend on mobileFrameWidth: this text is
  // short and never wraps differently at different widths, unlike the step text above.
  useLayoutEffect(() => {
    let cancelled = false;
    const measure = (): void => {
      if (cancelled) return;
      setMobileHeaderTextHeight(mobileHeaderTextRef.current?.offsetHeight ?? 0);
    };
    measure();
    void document.fonts?.ready.then(measure);
    return () => {
      cancelled = true;
    };
  }, []);

  // Scroll progress is a pure function of where the visible track sits — nothing is remembered.
  // Updates from scroll events are committed synchronously (flushSync) so the highlighted step and
  // the flip angle are rendered in the same frame as the scroll position, never a frame behind.
  const lastProgressRef = useRef(-1);
  useEffect(() => {
    const compute = (sync: boolean): void => {
      const desktop = window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH_PX}px)`).matches;
      const track = desktop ? desktopTrackRef.current : mobileTrackRef.current;
      const runway = desktop ? desktopRunwayRef.current : mobileRunwayRef.current;
      if (!track || !runway) return;
      const runwayPx = runway.offsetHeight;
      if (runwayPx <= 0) return;
      const pinTop = desktop ? 0 : MOBILE_PIN_TOP_PX;
      const progress = Math.max(0, Math.min(1, (pinTop - track.getBoundingClientRect().top) / runwayPx));
      const next = progress * STEP_COUNT;
      if (next === lastProgressRef.current) return;
      lastProgressRef.current = next;
      if (sync) flushSync(() => setStageProgress(next));
      else setStageProgress(next);
    };

    const onScroll = (): void => compute(true);
    compute(false); // first pass runs inside an effect, where a synchronous flush is not allowed
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const scrollToStepIndex = (targetIndex: number): void => {
    const desktop = window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH_PX}px)`).matches;
    const track = desktop ? desktopTrackRef.current : mobileTrackRef.current;
    const runway = desktop ? desktopRunwayRef.current : mobileRunwayRef.current;
    if (!track || !runway) return;
    const pinTop = desktop ? 0 : MOBILE_PIN_TOP_PX;
    const clampedIndex = Math.max(0, Math.min(MAX_STAGE, targetIndex));
    const trackTopAbs = window.scrollY + track.getBoundingClientRect().top;
    const target = trackTopAbs - pinTop + ((clampedIndex + 0.5) / STEP_COUNT) * runway.offsetHeight;
    window.scrollTo({ top: target, behavior: 'smooth' });
  };

  // ── Values derived from progress ───────────────────────────────────────────────────────────────
  const { baseStage, flipT, flipDeg } = flipForProgress(stageProgress, MAX_STAGE);
  const activeIndex = Math.min(MAX_STAGE, Math.floor(stageProgress));
  const stepProgress = stageProgress >= STEP_COUNT ? 1 : stageProgress - Math.floor(stageProgress);
  const faces = flipFaceSources(baseStage, MAX_STAGE);

  // Per-step visibility, shared by the mobile crossfade, the desktop nav and the loader tracks. The
  // array keeps its identity while every value is unchanged (most of the scroll), so memoized
  // children skip those frames entirely.
  const stageOpacitiesRef = useRef<number[]>([]);
  const stageOpacities = useMemo<number[]>(() => {
    const next = STEPS.map((_, k) => navStageOpacity(stageProgress, k, STEP_COUNT));
    const prev = stageOpacitiesRef.current;
    if (prev.length === next.length && prev.every((v, i) => v === next[i])) return prev;
    stageOpacitiesRef.current = next;
    return next;
  }, [stageProgress]);

  // The most visible step is the one to announce, so aria-current never runs ahead of what is seen.
  const visibleStepIndex = useMemo<number>(() => {
    let best = 0;
    for (let k = 1; k < stageOpacities.length; k++) {
      if (stageOpacities[k] > stageOpacities[best]) best = k;
    }
    return best;
  }, [stageOpacities]);

  const baseImageColumn = imageColForStage(baseStage);
  const nextImageColumn = baseStage + 1 >= STEP_COUNT ? baseImageColumn : imageColForStage(baseStage + 1);
  const imageColumnVisual = baseImageColumn + (nextImageColumn - baseImageColumn) * flipT;

  // Each step's visual column interpolates between its current- and next-stage column with the same
  // scroll-driven flip progress as the image; steps that land in the image's column pass beneath it.
  const stepColsVisual = useMemo<number[]>(
    () =>
      STEPS.map((_, k) => {
        const current = stepColAt(k, baseStage);
        const next = stepColAt(k, baseStage + 1);
        return current + flipT * (next - current);
      }),
    [baseStage, flipT],
  );

  // The nav row slides between its per-step offsets during the same flip windows as the card, so it is
  // fully scroll-linked (and reverses cleanly on scroll-back).
  // Left edge of each title in the row, from the measured natural widths.
  const navTitleLefts = useMemo(() => {
    const lefts: number[] = [];
    let x = 0;
    for (const w of navTitleWidths) {
      lefts.push(x);
      x += w + NAV_TITLE_GAP;
    }
    return lefts;
  }, [navTitleWidths]);
  const navOffsets = useMemo(() => navRowOffsets(navTitleLefts, navTitleWidths), [navTitleLefts, navTitleWidths]);
  const navRowOffset =
    navOffsets[baseStage] + (navOffsets[Math.min(baseStage + 1, MAX_STAGE)] - navOffsets[baseStage]) * flipT;

  const desktopLoaderStages = useMemo(() => {
    if (inPlaceCrossfadeOpacities(stageProgress)) return [IN_PLACE_FROM_STAGE, IN_PLACE_FROM_STAGE + 1];
    const stages: number[] = [];
    for (let k = 0; k < stageOpacities.length; k++) {
      if (stageOpacities[k] > 0.001) stages.push(k);
    }
    return stages;
  }, [stageProgress, stageOpacities]);

  const loaderFillPct = loaderFillPctFromProgress(stepProgress);
  const loaderBarOpacity = stageOpacities[activeIndex] ?? 1;

  // The frame is authored at 430px wide and scaled to fit; on short phones the scaled frame would be
  // taller than the pinned stage, so the illustration takes whatever height is left. Pure CSS (svh)
  // so it needs no viewport measurement and cannot change while the browser toolbar collapses.
  const mobileImageMaxHeight = `max(${MOBILE_IMAGE_MIN_HEIGHT_PX}px, calc((100svh - ${
    MOBILE_PIN_TOP_PX + MOBILE_PIN_BOTTOM_PX
  }px) / ${mobileScale} - ${MOBILE_NON_IMAGE_CONTENT_PX}px))`;

  return (
    <section className="relative mt-[50px] w-full overflow-visible bg-white sm:mt-[75px] md:mt-[100px] max-lg:pb-5 max-[380px]:pb-4 lg:mt-0 lg:pb-[80px]">
      {/* ───────────────────────────── Mobile / tablet ───────────────────────────── */}
      <div ref={mobileTrackRef} className="lg:hidden">
        <div
          data-process-stage=""
          className="sticky z-[2] w-full bg-white px-5 pb-3"
          style={{ top: MOBILE_PIN_TOP_PX }}
        >
          <div className="mx-auto overflow-hidden" style={{ width: mobileFrameWidth, height: mobileFrameHeight }}>
            <div
              ref={mobileContentRef}
              className="flex flex-col justify-between"
              style={{
                width: MOBILE_REF_WIDTH_PX,
                minHeight: `calc((100svh - ${MOBILE_PIN_TOP_PX + MOBILE_PIN_BOTTOM_PX}px) / ${mobileScale})`,
                transform: `scale(${mobileScale})`,
                transformOrigin: 'top left',
              }}
            >
              <div className="flex shrink-0 flex-col">
                {/* Reserves this block's true (counter-scaled) height in reference-space, so the
                    `mt-4` gap below starts from where the text actually ends on screen — see the
                    mobileHeaderTextHeight effect above. */}
                <div style={{ height: mobileHeaderTextHeight ? mobileHeaderTextHeight / mobileScale : undefined }}>
                  <div
                    ref={mobileHeaderTextRef}
                    className="flex flex-col gap-[4px]"
                    style={inverseScaleStyle(mobileScale, 'top left')}
                  >
                    <div className="border-l-2 border-prople-500 pl-[6px]">
                      <span className="ds-text-title-base text-gray-900">The process</span>
                    </div>
                    <h2 className="ds-text-title-2xl text-gray-900">
                      How it works
                    </h2>
                  </div>
                </div>

                <div className="mt-4">
                  <div
                    className="h-[3px] w-full overflow-hidden rounded-2 bg-prople-50"
                    style={{ opacity: loaderBarOpacity }}
                  >
                    <div className="h-full rounded-2 bg-prople-500" style={{ width: `${loaderFillPct}%` }} />
                  </div>
                </div>
              </div>

              {/* justify-start, not -between: with the text above the image (swapped per an earlier
                  request), -between pinned the image to the very bottom of this flex-1 box, leaving a
                  large gap between the description and the image instead of just the gap below it. */}
              {/* gap-3, not the earlier gap-6: this whole block is authored at 430px and scaled up by
                  mobileScale, which keeps growing all the way to the lg breakpoint (1024px) — at an
                  ordinary tablet width (~800px) that scale is ~1.77x, so a 24px authored gap rendered
                  as ~42px, reading as a large gap even though the source value looked fine at 430px. */}
              <div className="mt-6 flex min-h-0 flex-1 flex-col justify-start gap-3">
                <div
                  className="relative shrink-0"
                  // mobileStepTextHeight is measured off a counter-scaled (true-size) element, but
                  // THIS container has no counter-scale of its own — it's still inside the ambient
                  // `scale(mobileScale)` on mobileContentRef, which will shrink whatever height we set
                  // here. Dividing by mobileScale pre-compensates, so after the ambient transform
                  // shrinks it back down, the final on-screen height still equals the true, measured
                  // amount the counter-scaled text actually needs.
                  style={{ minHeight: mobileStepTextHeight ? mobileStepTextHeight / mobileScale : 148 }}
                >
                  {STEPS.map((step, k) => (
                    <div
                      key={step.id}
                      ref={(el) => {
                        mobileStepTextRefs.current[k] = el;
                      }}
                      className="absolute inset-x-0 top-0 flex flex-col gap-2 px-1"
                      style={{
                        opacity: stageOpacities[k],
                        pointerEvents: stageOpacities[k] > 0.01 ? 'auto' : 'none',
                        ...inverseScaleStyle(mobileScale, 'top left'),
                        // Caps this at the frame's REAL on-screen width (mobileFrameWidth is already
                        // a real px number, not a 430px-reference one) — without it, the counter-scaled
                        // text renders at its full 430px-reference width regardless of how narrow the
                        // actual phone is, running off the right edge on any phone under ~430px wide.
                        maxWidth: mobileFrameWidth,
                      }}
                      aria-hidden={stageOpacities[k] < 0.01}
                    >
                      <ProcessStepHeading number={step.number} title={step.title} className="mb-0 !min-h-[36px]" />
                      <MobileStepBody step={step} />
                    </div>
                  ))}
                </div>
                <MobileProcessVisual opacities={stageOpacities} maxHeight={mobileImageMaxHeight} />
              </div>
            </div>
          </div>
        </div>
        {/* Scroll runway: how far the visitor scrolls while the stage is pinned (one screen per step). */}
        <div ref={mobileRunwayRef} aria-hidden style={{ height: `${STEP_COUNT * 100}svh` }} />
      </div>

      {/* ───────────────────────────────── Desktop ───────────────────────────────── */}
      <div className="hidden lg:block">
        {/* Header scrolls away naturally; it is authored on the 1440px canvas and scaled with the stage. */}
        {/* z-[3]: the header's text box (100 + 24 + 12 + 52 = 188px) is 15px taller than this block, so it
            must paint above the sticky stage's white background. */}
        <div className="relative z-[3] mx-auto" style={{ width: 1440 * scale, height: DESKTOP_HEADER_H * scale }}>
          <div style={{ width: 1440, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
            <div style={{ paddingLeft: 100, paddingTop: 100 }}>
              {/* Position (this padding) tracks the ambient scale above, same as everywhere else in
                  this frame; the text itself is counter-scaled so it renders at its true, declared
                  size everywhere — see scale-compensate.ts. */}
              <div className="flex flex-col gap-[12px]" style={inverseScaleStyle(scale, 'top left')}>
                <div className="border-l-2 border-prople-500 pl-[8px]">
                  <span className="ds-text-title-base text-gray-900">The process</span>
                </div>
                <h2 className="ds-text-title-4xl text-gray-900">
                  How it works
                </h2>
              </div>
            </div>
          </div>
        </div>

        <div ref={desktopTrackRef}>
          <div data-process-stage="" className="sticky top-0 z-[2] h-screen overflow-hidden bg-white">
            {/* The stage: a 1240×888 frame centred in the window and scaled to fit it. */}
            <div
              className="absolute left-1/2 top-1/2"
              style={{
                width: DESKTOP_FRAME_W,
                height: DESKTOP_FRAME_H,
                transform: `translate(-50%, -50%) scale(${scale})`,
                transformOrigin: 'center center',
              }}
            >
              {/* Step nav: natural-width titles (text-title/6xl) in one row that slides left as the
                  sequence advances. Not clipped here: the sticky stage clips at the window edge, so the
                  next title bleeds in from the right exactly as in the design. */}
              <div className="absolute left-0 w-full" style={{ top: NAV_TOP, height: NAV_H }}>
                {/* absolute inset-0, not a plain wrapper: a `transform` (below) makes an element the
                    containing block for its `position: absolute` descendants, so without this the
                    titles' `left` values would resolve against this wrapper's own (content-less,
                    ~0-size) box instead of the NAV_TOP/NAV_H box above. inset-0 reproduces that exact
                    box here, so every title's `left` keeps meaning what it already means — see
                    scale-compensate.ts for why the whole group is counter-scaled as one block instead
                    of per-title (their `left`s are relative to each other's measured widths, which
                    must all stay in the same, single scale). */}
                <div className="absolute inset-0" style={inverseScaleStyle(scale, 'top left')}>
                {STEPS.map((step, stepIndex) => {
                  const stageOpacity = stageOpacities[stepIndex];
                  const left = navTitleLefts[stepIndex] + navRowOffset;
                  const width = navTitleWidths[stepIndex];
                  // Only titles that are inside the 1240px frame are reachable; the others are clipped.
                  const isOutsideFrame = left + width <= 0 || left >= DESKTOP_FRAME_W;

                  return (
                    <button
                      key={step.id}
                      ref={(el) => {
                        navButtonRefs.current[stepIndex] = el;
                      }}
                      type="button"
                      onClick={() => scrollToStepIndex(stepIndex)}
                      className={`pointer-events-auto absolute top-0 flex flex-col gap-[20px] ${
                        stageOpacity > 0.001 ? 'z-[1]' : 'z-0'
                      }`}
                      style={{
                        left,
                        width: 'max-content',
                        // Smooth brand ↔ inactive colour blend as a step becomes active.
                        color: `color-mix(in srgb, var(--ds-prople-500) ${Math.round(stageOpacity * 100)}%, var(--ds-blue-bell-400))`,
                        willChange: 'left, color',
                      }}
                      aria-current={stepIndex === visibleStepIndex ? 'step' : undefined}
                      aria-hidden={isOutsideFrame ? true : undefined}
                      tabIndex={isOutsideFrame ? -1 : undefined}
                    >
                      <span className="ds-text-title-6xl whitespace-nowrap">{step.title}</span>
                      <div className="h-[4px] w-full" />
                    </button>
                  );
                })}

                {desktopLoaderStages.map((stageIndex) => (
                  <NavLoaderTrack
                    key={`loader-${stageIndex}`}
                    leftPx={navTitleLefts[stageIndex] + navRowOffset}
                    widthPx={navTitleWidths[stageIndex]}
                    fillPct={loaderFillPctFromProgress(progressForStage(stageProgress, stageIndex))}
                    opacity={stageOpacities[stageIndex]}
                  />
                ))}
                </div>
              </div>

              {/* Content row: previous/next captions slide past the centred flip card. */}
              <div className="absolute left-0 w-full overflow-hidden" style={{ top: ROW_TOP, height: ROW_H }}>
                <div className="relative flex h-full w-full items-center justify-center">
                  <div className="invisible flex justify-center">
                    <ImagePlaceholder />
                  </div>

                  {STEPS.map((step, k) => (
                    <div
                      key={step.id}
                      className="absolute inset-y-0 z-0 flex items-center justify-center px-[12px]"
                      style={{
                        left: `${(stepColsVisual[k] * 100) / 3}%`,
                        width: `${100 / 3}%`,
                        willChange: 'left',
                      }}
                    >
                      {/* maxWidth caps this at the column's REAL on-screen width (a real px number,
                          not a frame-space one) — StepCard's own max-w-[420px] is a true-size value,
                          which is wider than the column can actually afford once `scale` drops below
                          1 (any desktop window narrower/shorter than the 1440×888 design). Without
                          this cap the counter-scaled card renders at its full true width regardless of
                          how little room is really available, overlapping its neighbours — the same
                          failure mode as text overflowing a shrunk phone screen; see the maxWidth on
                          the mobile step body below for the same fix in the mobile frame. */}
                      <div
                        style={{ ...inverseScaleStyle(scale, 'center center'), maxWidth: (DESKTOP_FRAME_W / 3) * scale }}
                      >
                        <StepCard step={step} />
                      </div>
                    </div>
                  ))}

                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-y-0 left-0 z-30 flex items-center justify-center"
                    style={{
                      width: `${100 / 3}%`,
                      transform: `translateX(${imageColumnVisual * 100}%)`,
                      willChange: 'transform',
                    }}
                  >
                    {/* Desktop-only: never mount (or fetch) these images on phones. */}
                    {isDesktop ? (
                      <DesktopFlipImage frontSrc={faces.front} backSrc={faces.back} flipDeg={flipDeg} />
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Scroll runway: one screen of scrolling per step while the stage is pinned. */}
          <div ref={desktopRunwayRef} aria-hidden style={{ height: `${STEP_COUNT * 100}vh` }} />
        </div>
      </div>
    </section>
  );
}
